import { Check, MessageSquareText, Paperclip, X } from "lucide-react";
import { useState, type FC } from "react";
import { toast } from "sonner";

import { ForeignNationalDocumentsAPI } from "@/api/foreignNationalDocumentsApi";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FirmCorrectionsAPI } from "@/features/clientPortal/api";
import { en } from "@/features/clientPortal/i18n/en";
import { fieldDisplay } from "@/features/clientPortal/profileModel";
import { useFirmCorrections } from "@/features/clientPortal/queries";
import type { PortalCorrection } from "@/features/clientPortal/types";

import { SectionCard } from "../../components/SectionCard";
import { useClientProfile } from "../../context/ClientProfileContext";

const FIELD_LABELS: Record<string, string> = {
  ...en.profile.field,
  other_names: "Other names",
  citizenships: "Citizenship",
  physical_address: "Home address",
  mailing_address: "Mailing address"
};

const openDocument = async (clientId: string, documentId: string) => {
  const tab = window.open("", "_blank");
  const blob = await ForeignNationalDocumentsAPI.getFileBlob(
    clientId,
    documentId
  );
  const url = URL.createObjectURL(blob);
  if (tab) tab.location.href = url;
  else window.location.assign(url);
};

const CorrectionItem: FC<{
  clientId: string;
  correction: PortalCorrection;
  onDecided: () => Promise<void>;
}> = ({ clientId, correction, onDecided }) => {
  const fields = Object.keys(correction.proposed);
  const [chosen, setChosen] = useState<string[]>(fields);
  const [declining, setDeclining] = useState(false);
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const history = correction.topic === "history";

  const run = (work: Promise<unknown>, done: string) => {
    setBusy(true);
    work
      .then(() => {
        toast.success(done);
        return onDecided();
      })
      .catch((error: unknown) =>
        toast.error(
          error instanceof Error ? error.message : "Couldn't save. Try again."
        )
      )
      .finally(() => setBusy(false));
  };
  const accept = () =>
    run(
      FirmCorrectionsAPI.accept(
        clientId,
        correction.correction_id,
        history ? undefined : chosen
      ),
      history ? "Marked handled" : "Profile updated"
    );
  const decline = () =>
    run(
      FirmCorrectionsAPI.decline(
        clientId,
        correction.correction_id,
        why.trim() || undefined
      ),
      "Correction declined"
    );

  return (
    <li
      className="flex flex-col gap-2.5 border-t border-border py-3 first:border-t-0 first:pt-0"
      data-testid="correction-item"
    >
      <span className="text-sm font-medium">{correction.label}</span>
      {fields.length ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              {fields.length > 1 ? (
                <th className="w-6">
                  <span className="sr-only">Accept</span>
                </th>
              ) : null}
              <th className="py-1 pr-3 font-normal">Field</th>
              <th className="py-1 pr-3 font-normal">On file</th>
              <th className="py-1 font-normal">Suggested</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => (
              <tr key={field} className="align-top">
                {fields.length > 1 ? (
                  <td className="py-1">
                    <Checkbox
                      aria-label={`Accept ${FIELD_LABELS[field] ?? field}`}
                      checked={chosen.includes(field)}
                      onCheckedChange={(on) =>
                        setChosen((prev) =>
                          on
                            ? [...prev, field]
                            : prev.filter((f) => f !== field)
                        )
                      }
                    />
                  </td>
                ) : null}
                <td className="py-1 pr-3 text-muted-foreground">
                  {FIELD_LABELS[field] ?? field}
                </td>
                <td className="py-1 pr-3 text-muted-foreground line-through">
                  {fieldDisplay(field, correction.current[field], "en-US") ||
                    "—"}
                </td>
                <td className="py-1 font-medium">
                  {fieldDisplay(field, correction.proposed[field], "en-US") ||
                    "(cleared)"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {correction.reason ? (
        <p className="flex gap-2 text-sm whitespace-pre-line text-muted-foreground">
          <MessageSquareText className="mt-0.5 size-3.5 shrink-0" />
          {correction.reason}
        </p>
      ) : null}
      {correction.document_id ? (
        <Button
          size="xs"
          variant="outline"
          className="w-fit"
          onClick={() =>
            void openDocument(clientId, correction.document_id ?? "").catch(
              () => toast.error("Couldn't open the document.")
            )
          }
        >
          <Paperclip /> Open attached document
        </Button>
      ) : null}
      {declining ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`decline-${correction.correction_id}`}>
            Tell the client why (optional, they see this)
          </Label>
          <Textarea
            id={`decline-${correction.correction_id}`}
            rows={2}
            value={why}
            onChange={(e) => setWhy(e.target.value)}
          />
        </div>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        {declining ? (
          <>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => setDeclining(false)}
            >
              Back
            </Button>
            <Button
              size="xs"
              variant="destructive"
              disabled={busy}
              onClick={decline}
            >
              <X /> Decline
            </Button>
          </>
        ) : (
          <>
            <Button
              size="xs"
              variant="ghost"
              disabled={busy}
              onClick={() => setDeclining(true)}
            >
              Decline…
            </Button>
            <Button
              variant="primary"
              size="xs"
              disabled={busy || (!history && !chosen.length)}
              onClick={accept}
              data-testid="correction-accept"
            >
              <Check />
              {history
                ? "Mark handled"
                : chosen.length < fields.length
                ? `Accept ${chosen.length} of ${fields.length}`
                : "Accept"}
            </Button>
          </>
        )}
      </div>
    </li>
  );
};

/** Corrections the client suggested from the portal, waiting for review.
 * Renders nothing when there are none. */
export const CorrectionsReviewCard: FC = () => {
  const { clientId, client, reloadClient } = useClientProfile();
  const corrections = useFirmCorrections(clientId);
  const pending = (corrections.data ?? []).filter(
    (c) => c.status === "pending"
  );
  if (!pending.length) return null;
  const first = (client?.first_name ?? "").trim() || "The client";
  const onDecided = async () => {
    await Promise.all([corrections.refetch(), reloadClient()]);
  };
  return (
    <SectionCard
      title={`${first} suggested ${
        pending.length === 1 ? "a correction" : `${pending.length} corrections`
      }`}
    >
      <ul className="flex flex-col">
        {pending.map((c) => (
          <CorrectionItem
            key={c.correction_id}
            clientId={clientId}
            correction={c}
            onDecided={onDecided}
          />
        ))}
      </ul>
    </SectionCard>
  );
};
